import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import styled from 'styled-components';
import { BACKGROUND_GRAY, INACTIVE_GRAY, NIGHTINGALE_BLACK, RIOJA_RED, SENSATION_WHITE } from '../../colors';
import { getMs, getPx } from '../../helpers/common';
import { INPUT_HALF_HEIGHT, INPUT_HORIZONTAL_CONDENSED_PADDING, INPUT_HORIZONTAL_MINIMAL_PADDING, INPUT_PLACEHOLDER_WITH_ICON_SHIFT, INPUT_SHRINK_ANIMATION_DURATION, INPUT_SHRINK_ANIMATION_FUNCTION, INPUT_SHRINK_TEXT_SCALE, INPUT_TEXT_WITH_ICON_LEFT_PADDING, INPUT_TEXT_WITH_ICON_LEFT_PADDING_DELTA, INPUT_TEXT_WITH_ICON_LEFT_PADDING_SHRINKED } from '../../Input/constants';
import { Input as InputTypographySize } from '../../Typography/constants/input.size';
import { baseTextStyle } from '../../Typography/style/base';
import { userSelectNoneStyle } from '../../tokens/user-select';
import { pointerEventsNoneStyle } from '../../tokens/pointer-events';
import { baseEllipsisStyle } from '../../tokens/ellipsis';
import { baseHighlightStyle } from '../../tokens/highlight';
import { height } from '../../style/height';
import { maxWidth } from '../../style/max-width';
import { translateY } from '../../style/translate-y';
import { translateX } from '../../style/translate-x';
import { lineHeight } from '../../style/line-height';
import { fontSize } from '../../style/font-size';
import { paddingLeft } from '../../style/padding-left';
import { paddingRight } from '../../style/padding-right';
import { marginRightNegative } from '../../style/margin-right-negative';
export const FieldLabelOuter = styled.div`
  position: relative;
  width: 100%;
`;
export const FieldLabelPlaceholder = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  transform: translateY(-50%);
  ${height(InputTypographySize.lineHeight)}
`;
export const FieldLabelWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  ${paddingLeft({
  xs: 16,
  md: 20,
  lg: 15,
  xl: 15
})}
  transition: transform ${getMs(INPUT_SHRINK_ANIMATION_DURATION)} ${INPUT_SHRINK_ANIMATION_FUNCTION};
  position: absolute;
  height: 0;
  top: 0;
  left: 0;
  right: 0;
  ${({
  $modifier,
  $condensed
}) => $condensed && ($modifier === 'from' || $modifier === 'single') && `
    padding-right: ${getPx(INPUT_HORIZONTAL_MINIMAL_PADDING)};
  `}
  ${({
  $modifier,
  $condensed
}) => $condensed && $modifier === 'to' && `
    padding-left: ${getPx(INPUT_HORIZONTAL_CONDENSED_PADDING)};
    padding-right: ${getPx(INPUT_HORIZONTAL_MINIMAL_PADDING)};
  `}
  ${({
  $withIcon,
  $shrink
}) => $withIcon && paddingLeft($shrink ? INPUT_TEXT_WITH_ICON_LEFT_PADDING_SHRINKED : INPUT_TEXT_WITH_ICON_LEFT_PADDING)}
  ${({
  $withRightIcon,
  $shrink
}) => !$shrink && $withRightIcon && paddingRight(INPUT_TEXT_WITH_ICON_LEFT_PADDING)}
  
  /* special fix for shrinked placeholder to avoid unnecessary ellipsis */
  ${({
  $withIcon,
  $shrink
}) => $withIcon && $shrink && marginRightNegative(INPUT_TEXT_WITH_ICON_LEFT_PADDING_DELTA)}
  
  ${userSelectNoneStyle}
  ${pointerEventsNoneStyle}
  ${translateY(INPUT_HALF_HEIGHT)}
  ${({
  $shrink,
  $withIcon
}) => $shrink && ($withIcon ? translateX(INPUT_PLACEHOLDER_WITH_ICON_SHIFT) : `transform: translateY(0) !important;`)}
`;
export const FieldLabel = styled.div`
  position: absolute;
  top: 0;
  ${({
  $shrink
}) => $shrink ? maxWidth({
  xs: 'calc(125% - 10px)',
  md: 'calc(125% - 15px)',
  lg: 'calc(125% - 7px)',
  xl: 'calc(125% - 7px)'
}) : 'max-width: 100%;'}
  display: inline-block;
  color: ${({
  $error,
  $shrink
}) => $error && $shrink ? RIOJA_RED : INACTIVE_GRAY};
  background-color: ${SENSATION_WHITE};
  padding: 0 4px !important;
  margin: 0 -8px !important;
  border-radius: 6px;
  transition: transform ${getMs(INPUT_SHRINK_ANIMATION_DURATION)} ${INPUT_SHRINK_ANIMATION_FUNCTION},
    color ${getMs(INPUT_SHRINK_ANIMATION_DURATION)} ${INPUT_SHRINK_ANIMATION_FUNCTION},
    max-width ${getMs(INPUT_SHRINK_ANIMATION_DURATION)} ${INPUT_SHRINK_ANIMATION_FUNCTION};
  transform-origin: left center;
  ${({
  $disabled
}) => $disabled && `background-color: ${BACKGROUND_GRAY};`}
  ${fontSize(InputTypographySize.fontSize)}
  ${lineHeight(InputTypographySize.lineHeight)}
  ${baseTextStyle}
  ${baseEllipsisStyle}
  ${baseHighlightStyle}
  ${userSelectNoneStyle}
  ${pointerEventsNoneStyle}
  ${({
  $shrink
}) => $shrink && `transform: scale(${INPUT_SHRINK_TEXT_SCALE});`}
  ${({
  $shrink,
  $condensed
}) => $shrink && $condensed && maxWidth({
  xs: 'calc(100% + 14px)',
  md: 'calc(100% + 16px)',
  xl: 'calc(100% + 18px)'
})}
  ${({
  $focused,
  $shrink
}) => $focused && $shrink && `color: ${NIGHTINGALE_BLACK};`}
`;
const FormLabel = ({
  modifier = 'single',
  condensed = false,
  error,
  focused = false,
  withIcon,
  withRightIcon,
  shrink = false,
  multiline,
  disabled = false,
  placeholder,
  ...props
}) => placeholder ? /*#__PURE__*/React.createElement(FieldLabelWrapper, _extends({
  $modifier: modifier,
  $condensed: condensed,
  $withIcon: withIcon,
  $withRightIcon: withRightIcon,
  $shrink: shrink
}, props), /*#__PURE__*/React.createElement(FieldLabelOuter, null, /*#__PURE__*/React.createElement(FieldLabelPlaceholder, null, /*#__PURE__*/React.createElement(FieldLabel, {
  as: 'span',
  $error: error,
  $focused: focused,
  $multiline: multiline,
  $disabled: disabled,
  $shrink: shrink,
  $condensed: condensed
}, placeholder)))) : null;
export default FormLabel;